import React from 'react';
import { Bus, Train, Ship, Pencil, Trash2, Eye } from 'lucide-react';
import { useTranslation } from '../../useTranslation';
import { useFx } from '../../hooks/FxRatesContext';
import type { GroundTrip, StationInfo } from '../../types/ground';
import { softBreakAfterHyphens, minutesToFormatted } from './groundTripUtils';
import { TripRouteTile } from '../common/TripRouteTile';
import { TagBadge } from '../common/TagBadge';
import { groundModeTheme } from '../common/tripTileThemes';
import {
  airportNameLabel,
  cityCountryLabel,
} from '../../utils/airportLabels';
import {
  addDaysIso,
  arrivalDayOffset,
} from '../../utils/flightDayOffset';

function stationEndpoint(
  info: StationInfo | null | undefined,
  cityField: string,
  useRu: boolean,
  alignRight = false,
) {
  const city = cityCountryLabel(info, cityField, useRu);
  const name = airportNameLabel(info, cityField, useRu);
  const title = [name, city].filter(Boolean).join(' · ') || cityField;
  const primary = name || city || cityField;
  const secondary = name && city && name !== city ? city : !name && city ? null : city !== primary ? city : null;

  return (
    <div className={alignRight ? 'text-right' : ''}>
      <p
        className={`text-base sm:text-lg font-semibold text-stone-900 dark:text-white leading-snug [overflow-wrap:anywhere] line-clamp-2 ${alignRight ? 'text-right' : ''}`}
        title={title}
      >
        {softBreakAfterHyphens(primary)}
      </p>
      {secondary ? (
        <p
          className={`text-xs text-stone-500 dark:text-slate-400 mt-0.5 leading-snug line-clamp-2 ${alignRight ? 'text-right' : ''}`}
          title={secondary}
        >
          {softBreakAfterHyphens(secondary)}
        </p>
      ) : null}
    </div>
  );
}

export const GroundTripCard: React.FC<{
  trip: GroundTrip;
  selected?: boolean;
  onSelect?: () => void;
  onView?: (trip: GroundTrip) => void;
  onEdit?: (trip: GroundTrip) => void;
  onDelete?: (trip: GroundTrip) => void;
}> = ({ trip, selected, onSelect, onView, onEdit, onDelete }) => {
  const lang = localStorage.getItem('travelDiarySettings')
    ? JSON.parse(localStorage.getItem('travelDiarySettings')!).language
    : 'ru-RU';
  const { t, formatDate, formatNumber, formatDistance } = useTranslation(lang);
  const { toDisplay, displaySymbol } = useFx();
  const settings = localStorage.getItem('travelDiarySettings')
    ? JSON.parse(localStorage.getItem('travelDiarySettings')!)
    : { distanceUnit: 'km', currency: '₽' };
  const theme = groundModeTheme(trip.mode);
  const RouteIcon = trip.mode === 'bus' ? Bus : trip.mode === 'ferry' ? Ship : Train;
  const durationLabel = trip.formattedDuration || minutesToFormatted(trip.duration);
  const isTrain = trip.mode === 'train';
  const useRu = lang.toLowerCase().startsWith('ru');
  const branded = trip.trainName?.trim() || '';
  const carrier = trip.operator?.trim() || '';
  const number = trip.routeNumber?.trim() || '';
  const priceLabel =
    trip.price != null && trip.price > 0
      ? `${formatNumber(toDisplay(trip.price, 'RUB'))} ${displaySymbol}`
      : null;

  const dayOffset = isTrain
    ? arrivalDayOffset({
        departureTime: trip.departure_time,
        arrivalTime: trip.arrival_time,
        flightDate: trip.date,
      })
    : 0;
  const depTime = (trip.departure_time || '').trim();
  const arrTime = (trip.arrival_time || '').trim();
  const hasTimes = Boolean(depTime || arrTime);
  const arrivalDateIso = dayOffset > 0 ? addDaysIso(trip.date, dayOffset) : null;
  const dateLabel = arrivalDateIso
    ? `${formatDate(trip.date)} → ${formatDate(arrivalDateIso)}`
    : formatDate(trip.date);

  return (
    <TripRouteTile
      selected={selected}
      onSelect={onSelect}
      theme={theme}
      language={lang}
      routeIcon={RouteIcon}
      distanceLabel={
        trip.distance != null && trip.distance > 0
          ? formatDistance(trip.distance, settings.distanceUnit)
          : null
      }
      durationLabel={durationLabel || null}
      origin={stationEndpoint(trip.origin_station_info, trip.originCity, useRu)}
      destination={stationEndpoint(trip.destination_station_info, trip.destinationCity, useRu, true)}
      meta={
        <>
          {trip.tag && (
            <TagBadge tag={trip.tag} />
          )}
          {isTrain && branded && (
            <p
              className="text-sm font-semibold text-indigo-700 dark:text-indigo-300 leading-snug [overflow-wrap:anywhere] break-words"
              title={branded}
            >
              {softBreakAfterHyphens(branded)}
            </p>
          )}
          {(carrier || number) && (
            <p
              className="text-sm font-medium text-stone-800 dark:text-slate-100 leading-snug [overflow-wrap:anywhere] break-words"
              title={[carrier, number].filter(Boolean).join(' · ')}
            >
              {softBreakAfterHyphens([carrier, number].filter(Boolean).join(' · '))}
            </p>
          )}
          {hasTimes ? (
            <p className="text-xs text-stone-500 dark:text-slate-400 tabular-nums">
              {depTime || '—'}
              {arrTime ? (
                <>
                  {' – '}
                  {arrTime}
                  {dayOffset > 0 ? (
                    <span className="font-bold text-stone-700 dark:text-slate-200">+{dayOffset}</span>
                  ) : null}
                </>
              ) : null}
            </p>
          ) : null}
          <p className="text-xs text-stone-500 dark:text-slate-400">{dateLabel}</p>
          {priceLabel && (
            <p className={`text-base font-bold tabular-nums ${theme.price}`}>{priceLabel}</p>
          )}
        </>
      }
      actions={
        <>
          {onView && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onView(trip);
              }}
              className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-100 dark:hover:bg-slate-700 shrink-0"
              title={t.viewTrip}
            >
              <Eye className="h-4 w-4" />
            </button>
          )}
          {onEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(trip);
              }}
              className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-100 dark:hover:bg-slate-700 shrink-0"
              title={t.editTrip}
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(t.confirmDeleteTrip ?? 'Удалить?')) onDelete(trip);
              }}
              className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 shrink-0"
              title={t.deleteTrip}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </>
      }
    />
  );
};
